import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@/lib/api/client';
import { fetchBoardColumn } from '@/services/boardService';
import {
  TASK_STATUSES,
  type BoardColumnPage,
  type ColumnFilter,
  type Task,
  type TaskStatus,
} from '@/types/api';

/**
 * One infinite query per column.
 *
 * Per column, not one paged board, for two reasons. The columns scroll
 * independently — hitting the bottom of Completed must not re-fetch Pending. And
 * TanStack refetches ALL loaded pages of an infinite query on invalidation, which is
 * exactly what keeps a card the desks changed on page 3 correct without us tracking
 * which pages are open.
 */
export function useBoardColumn(
  status: TaskStatus,
  filter: ColumnFilter,
  assigneeId?: string,
): UseInfiniteQueryResult<{ pages: BoardColumnPage[] }, ApiError> {
  const scope = assigneeId ?? 'me';

  return useInfiniteQuery<BoardColumnPage, ApiError, { pages: BoardColumnPage[] }, unknown[], string | null>({
    /*
      The filter is part of the KEY, so changing it is a different question with its own
      cached answer — and only this column refetches. It also means going back to an
      earlier filter is instant rather than another round trip.
    */
    queryKey: ['boardColumn', scope, status, filter],
    initialPageParam: null,
    queryFn: ({ pageParam }) => fetchBoardColumn({ status, filter, cursor: pageParam, assigneeId }),
    getNextPageParam: (last) => last.nextCursor,
  });
}

export interface BoardColumnView {
  status: TaskStatus;
  filter: ColumnFilter;
  /** This column has no data for its CURRENT filter yet. */
  loading: boolean;
  /** True when this column is showing a subset, so the header can say so. */
  filtered: boolean;
  tasks: Task[];
  /** Across the whole column, NOT the loaded pages. */
  total: number;
  unread: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

export interface BoardView {
  columns: BoardColumnView[];
  total: number;
  unread: number;
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
  refetch: () => void;
}

/**
 * The five columns as one object, so the screens can stay unaware that each one is
 * paginated on its own.
 *
 * Hooks are called unconditionally in a fixed order — TASK_STATUSES is a constant, so
 * this is five literal calls as far as React is concerned, not a loop over data.
 */
export function useBoardColumns(
  filters: Record<TaskStatus, ColumnFilter>,
  assigneeId?: string,
): BoardView {
  const queries = TASK_STATUSES.map((status) =>
    useBoardColumn(status, filters[status], assigneeId),
  );

  const columns = queries.map((query, index): BoardColumnView => {
    const status = TASK_STATUSES[index] as TaskStatus;
    const pages = query.data?.pages ?? [];
    const last = pages[pages.length - 1];

    const filter = filters[status];

    return {
      status,
      filter,
      loading: query.isPending,
      filtered: Boolean(filter.priority?.length || filter.tag || filter.overdue),
      // Deduped by id: a card can move between pages between fetches, and rendering
      // the same card twice reads as data corruption.
      tasks: dedupe(pages.flatMap((page) => page.items)),
      total: last?.total ?? 0,
      unread: last?.unread ?? 0,
      hasMore: query.hasNextPage,
      isLoadingMore: query.isFetchingNextPage,
      loadMore: () => {
        if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
      },
    };
  });

  return {
    columns,
    total: columns.reduce((sum, column) => sum + column.total, 0),
    unread: columns.reduce((sum, column) => sum + column.unread, 0),
    /*
      Only the very first paint blocks the whole board.

      This used to be `.some(isPending)`, which was wrong the moment filters existed:
      a filter change is a new query key, so that column has no cached data and the
      entire board dropped to a skeleton — four columns that had not moved disappeared
      to answer a question about the fifth.

      `.every` means the full skeleton appears only when nothing has ever loaded. After
      that a column loads inside its own borders.
    */
    isPending: queries.every((query) => query.data === undefined),
    isError: queries.some((query) => query.isError),
    error: queries.find((query) => query.error)?.error ?? null,
    refetch: () => queries.forEach((query) => void query.refetch()),
  };
}

function dedupe(tasks: Task[]): Task[] {
  const seen = new Set<string>();
  return tasks.filter((task) => (seen.has(task.id) ? false : (seen.add(task.id), true)));
}
